import { useQuery } from '@tanstack/react-query';
import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { useLocation } from 'react-router';

import { type ApiProblem, apiProblemEvent } from '../api/client';
import avatar from '../assets/brand/maklerpass-icon.svg';
import { Icon } from '../components/icons';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { AssistantPanel } from './AssistantPanel';
import { pageContext } from './pageContext';
import { hintsEnabled, markTipSeen, markVoiceIntroSeen, problemQuestion, problemTopic, tipFor, tipSeen, voiceIntroSeen } from './proactive';
import { useAssistantChat } from './useAssistantChat';

interface Teaser {
  kind: 'tip' | 'problem' | 'voice';
  text: string;
  question: string;
}

const tipDelayMs = 1500;
const tipVisibleMs = 15000;
const problemPauseMs = 120000;

const teaserTitle: Record<Teaser['kind'], string> = { tip: 'Tipp der Assistentin', problem: 'Nicht geklappt?', voice: 'Neu: mit mir sprechen' };
const teaserAction: Record<Teaser['kind'], string> = { tip: 'Mehr dazu', problem: 'Erklären lassen', voice: 'Jetzt sprechen' };

// AssistantDock: KI-Hilfe als Chat-Box unten rechts (Dominik 16.10.2026) – Knopf zum Öffnen, proaktive Hinweise (einmal der
// Hinweis aufs Sprechen, Tipp je Seite einmal je Sitzung, Hilfe bei gesperrten oder fehlgeschlagenen Aktionen) und das
// Chatfenster mit Sprachassistentin. „Dass man reden kann, sieht man nicht“ (Dominik): Sprechen steht deshalb sichtbar an
// Knopf, Begrüßung, Eingabezeile und Kopf.
export function AssistantDock({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { project } = useProject();
  const papi = useProjectApi();
  const location = useLocation();
  const page = pageContext(location.pathname, location.search);
  const chat = useAssistantChat(page);
  const voice = useQuery({
    queryKey: projectKeys.assistantVoice(project.id),
    queryFn: ({ signal }) => papi.assistantVoiceStatus(signal),
    staleTime: 60_000,
  });
  const voiceOn = !!voice.data?.enabled;
  const [voiceMode, setVoiceMode] = useState(false);
  const [teaser, setTeaser] = useState<Teaser | null>(null);
  const lastProblem = useRef<Record<string, number>>({});

  // Einmal je Browser: Hinweis, dass man mit der Assistentin sprechen kann (vor den Seitentipps).
  useEffect(() => {
    if (!voiceOn || open || !hintsEnabled() || voiceIntroSeen()) return;
    const show = window.setTimeout(() => {
      markVoiceIntroSeen();
      setTeaser({ kind: 'voice', text: 'Fragen Sie einfach laut – ich antworte mit Stimme und öffne auf Wunsch die passende Seite.', question: '' });
    }, tipDelayMs);
    return () => window.clearTimeout(show);
  }, [voiceOn, open]);

  useEffect(() => {
    const tip = tipFor(page.key);
    if (open || !tip || !hintsEnabled() || tipSeen(page.key)) return;
    const show = window.setTimeout(() => {
      setTeaser((t) => {
        if (t) return t;
        markTipSeen(page.key);
        return { kind: 'tip', ...tip };
      });
    }, tipDelayMs + 400);
    return () => window.clearTimeout(show);
  }, [page.key, open]);

  useEffect(() => {
    if (teaser?.kind !== 'tip') return;
    const hide = window.setTimeout(() => setTeaser((t) => (t?.kind === 'tip' ? null : t)), tipVisibleMs);
    return () => window.clearTimeout(hide);
  }, [teaser]);

  const onProblem = useEffectEvent((p: ApiProblem) => {
    const topic = problemTopic(p);
    const now = Date.now();
    if (!topic || open || !hintsEnabled() || now - (lastProblem.current[p.code] ?? 0) < problemPauseMs) return;
    lastProblem.current[p.code] = now;
    setTeaser({ kind: 'problem', text: 'Ich erkläre, woran es liegt und was Sie tun können.', question: problemQuestion(topic) });
  });
  useEffect(() => {
    const handler = (e: Event) => onProblem((e as CustomEvent<ApiProblem>).detail);
    window.addEventListener(apiProblemEvent, handler);
    return () => window.removeEventListener(apiProblemEvent, handler);
  }, []);

  const accept = (t: Teaser) => {
    setTeaser(null);
    onOpenChange(true);
    if (t.kind === 'voice') setVoiceMode(true);
    else chat.send(t.question);
  };

  return (
    <>
      {!open && teaser && (
        <div className={`assistant-teaser ${teaser.kind}`} aria-live="polite">
          <img className="assistant-avatar" src={avatar} alt="" />
          <div className="assistant-teaser-body">
            <p className="assistant-teaser-title">{teaserTitle[teaser.kind]}</p>
            <p>{teaser.text}</p>
            <button type="button" className="btn btn-small btn-primary" onClick={() => accept(teaser)}>
              {teaser.kind === 'voice' && <Icon name="mic" size={16} />} {teaserAction[teaser.kind]}
            </button>
          </div>
          <button type="button" className="assistant-teaser-close" aria-label="Hinweis ausblenden" onClick={() => setTeaser(null)}>
            <Icon name="close" size={16} />
          </button>
        </div>
      )}
      {!open && (
        <button
          type="button"
          className="assistant-launcher"
          aria-label="KI-Hilfe öffnen"
          title={voiceOn ? 'KI-Hilfe – schreiben oder sprechen' : 'KI-Hilfe'}
          onClick={() => onOpenChange(true)}
        >
          <img src={avatar} alt="" />
          {voiceOn && (
            <span className="assistant-launcher-badge" aria-hidden="true">
              <Icon name="mic" size={13} />
            </span>
          )}
        </button>
      )}
      <AssistantPanel
        open={open}
        onClose={() => onOpenChange(false)}
        page={page}
        chat={chat}
        voice={voice.data}
        voiceMode={voiceMode}
        onVoiceMode={setVoiceMode}
      />
    </>
  );
}
